import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import { CrashTrigger } from "@/components/CrashTrigger";
import { StatusBanner } from "@/components/StatusBanner";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { MobileBottomBar } from "@/components/MobileBottomBar";
import { RedirectGate } from "@/components/RedirectGate";
import { LazyWidget } from "@/components/LazyWidget";
import { SentryContextBridge } from "@/components/SentryContextBridge";
import { trackEvent } from "@/lib/tracking";
import { BusinessConfigHydrator, useBusinessConfig } from "@/lib/use-business-config";
import {
  BING_VERIFICATION_CODE,
  GSC_VERIFICATION_CODE,
  ORGANIZATION_SCHEMA,
} from "@/lib/seo";

import appCss from "../styles.css?url";

/** Fires a page_view event whenever the URL pathname changes. */
function PageViewTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (typeof window === "undefined") return;
    trackEvent("page_view");
    // Lazy-init Core Web Vitals reporter on first navigation
    void import("@/lib/web-vitals-reporter").then((m) => m.startWebVitalsReporting());
  }, [pathname]);
  return null;
}

/**
 * Client-side canonical-host enforcement.
 *
 * Hosting-level 301s should be configured in Lovable → Project Settings →
 * Domains (set woodseerautos.co.uk as Primary). This component is a
 * belt-and-braces fallback for visitors who land on a non-canonical host
 * (apex, lovable.app preview shared publicly, etc.) — it redirects them to
 * the canonical www domain in production only.
 */
function CanonicalHostRedirect() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const { hostname, pathname, search, hash } = window.location;
    const CANONICAL = "woodseerautos.co.uk";
    // Canonical redirect should be handled in Lovable domain settings as the
    // primary 301. This client fallback is deliberately conservative to avoid
    // preview/admin reload loops.
    if (import.meta.env.DEV) return;
    if (hostname === CANONICAL) return;
    if (pathname.startsWith("/admin")) return;
    if (hostname.includes("lovable.app") || hostname.includes("lovable.dev") || hostname.includes("lovableproject.com")) return;
    try {
      if (sessionStorage.getItem("woodseer_canonical_redirect_attempted") === "1") return;
    } catch {}
    const isApex = hostname === "woodseerautos.co.uk";
    if (!isApex) return;
    try { sessionStorage.setItem("woodseer_canonical_redirect_attempted", "1"); } catch {}
    window.location.replace(`https://${CANONICAL}${pathname}${search}${hash}`);
  }, []);
  return null;
}

function CacheResetHandler() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("reset-cache") !== "1") return;
    const keys = [
      "woodseer_public_config_v1",
      "woodseer_public_config_v2",
      "woodseer_booking_flow_v1",
      "woodseer_review_strip_dismissed",
      "woodseer_canonical_redirect_attempted",
      "woodseer_session",
    ];
    try {
      keys.forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    } catch {}
    url.searchParams.delete("reset-cache");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);
  return null;
}

function NotFoundLogger() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = window.location.pathname;
    if (!p || p.startsWith("/admin") || p.startsWith("/api/")) return;
    try {
      fetch("/api/public/not-found-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: p, referer: document.referrer || null }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }, []);
  return null;
}

function NotFoundComponent() {
  const biz = useBusinessConfig();
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4 py-20">
      <NotFoundLogger />
      <div className="max-w-xl text-center">
        <p className="text-sm font-semibold text-primary uppercase tracking-widest">Page not found</p>
        <h1 className="mt-3 text-6xl md:text-7xl font-bold text-foreground">404</h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          That page doesn't exist — but we do. Head back to the homepage or give us a call and we'll help you find what you need.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Back to Homepage
          </Link>
          <a
            href={biz.phoneHref}
            className="inline-flex items-center justify-center rounded-md border border-border bg-secondary px-6 py-3 text-sm font-semibold text-secondary-foreground hover:bg-secondary/80 transition-colors"
          >
            Call {biz.phone}
          </a>
        </div>
        <div className="mt-10 pt-8 border-t border-border/60">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Popular pages</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
            <Link to="/mot" className="text-foreground hover:text-primary transition-colors font-semibold">MOT Testing</Link>
            <span className="text-border">·</span>
            <Link to="/servicing" className="text-foreground hover:text-primary transition-colors font-semibold">Servicing</Link>
            <span className="text-border">·</span>
            <Link to="/diagnostics" className="text-foreground hover:text-primary transition-colors font-semibold">Diagnostics</Link>
            <span className="text-border">·</span>
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors font-semibold">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Per-route title/description/OG come from seoHead(); only sitewide,
      // non-duplicating defaults live here to avoid concatenated meta on leaves.
      { name: "author", content: "Woodseer Autos" },
      { name: "theme-color", content: "#0F172A" },
      ...(GSC_VERIFICATION_CODE
        ? [{ name: "google-site-verification", content: GSC_VERIFICATION_CODE }]
        : []),
      ...(BING_VERIFICATION_CODE
        ? [{ name: "msvalidate.01", content: BING_VERIFICATION_CODE }]
        : []),
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Woodseer Autos Blog RSS Feed",
        href: "https://woodseerautos.co.uk/rss.xml",
      },
    ],
    scripts: [
      // Site-wide Organization JSON-LD.
      { type: "application/ld+json", children: JSON.stringify(ORGANIZATION_SCHEMA) },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function LovableBadgeGate() {
  // Only hide the badge on production builds — local dev keeps it visible so
  // we can spot Lovable runtime issues. Toggleable via VITE_HIDE_LOVABLE_BADGE.
  // When hidden, we also install a runtime blocker that removes any injected
  // badge node and aborts network requests for badge assets before they load.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const flag = import.meta.env.VITE_HIDE_LOVABLE_BADGE;
    const shouldHide = flag === "false" ? false : flag === "true" ? true : import.meta.env.PROD;
    if (shouldHide) {
      document.body.classList.add("hide-lovable-badge");
      void import("@/lib/lovable-badge-blocker").then((m) => m.installLovableBadgeBlocker());
    } else {
      document.body.classList.remove("hide-lovable-badge");
    }
  }, []);
  return null;
}

function RootComponent() {
  useEffect(() => {
    void import("@/lib/sentry").then((m) => m.initSentry());
  }, []);
  return (
    <GlobalErrorBoundary>
      <LovableBadgeGate />
      <SentryContextBridge />
      <BusinessConfigHydrator />
      <CacheResetHandler />
      <CanonicalHostRedirect />
      <RedirectGate />
      <PageViewTracker />
      <SiteHeader />
      <StatusBanner />
      <Breadcrumbs />
      <main className="pb-24 md:pb-0">
        <GlobalErrorBoundary>
          <CrashTrigger />
          <Outlet />
        </GlobalErrorBoundary>
      </main>
      <LazyWidget
        name="ReviewCTAStrip"
        loader={() => import("@/components/ReviewCTAStrip")}
        exportName="ReviewCTAStrip"
        timeoutMs={4000}
        deferMs={800}
      />
      <Footer />
      <MobileBottomBar />
      <LazyWidget
        name="WhatsAppFab"
        loader={() => import("@/components/WhatsAppFab")}
        exportName="WhatsAppFab"
        timeoutMs={5000}
        deferMs={1200}
      />
      <LazyWidget
        name="CookieBanner"
        loader={() => import("@/components/CookieBanner")}
        exportName="CookieBanner"
        timeoutMs={5000}
        deferMs={500}
      />
      <LazyWidget
        name="ExitIntentBar"
        loader={() => import("@/components/ExitIntentBar")}
        exportName="ExitIntentBar"
        timeoutMs={5000}
        deferMs={2000}
      />
    </GlobalErrorBoundary>
  );
}
